import { useEffect, useState } from "react";
import { Adapt, Sheet } from "tamagui";

import {
  useGetAvailability,
  useGetProviderReviews,
  useGetProviders,
} from "@/api/generated";
import type { GetProvidersZocdocProvidersGetParams } from "@/api/generated/models";
import { Dialog } from "@/components/Dialog";
import { useAnalytics } from "@/hooks/analytics";
import { useCurrentConversationId } from "@/hooks/conversation";
import { useZocdocBookingStore } from "@/store/zocdocBooking";
import { addDaysYMD, todayYMD } from "@/utils/datetime";
import { AvailabilityPhase } from "./AvailabilityPhase";
import { ConfirmPhase } from "./ConfirmPhase";
import { DayPhase } from "./DayPhase";
import { InsurancePhase } from "./InsurancePhase";
import { ProvidersPhase } from "./ProvidersPhase";
import { ZipPhase } from "./ZipPhase";

export const ZocdocBookingModalBody = () => {
  const {
    bookingContent,
    isOpen,
    eventId,
    phase,
    selectedPlanId,
    selectedCarrierName,
    zipCode,
    selectedProvider,
    providerParams,
    weekStartDate,
    selectedDate,
    selectedTimeslot,
    close,
    setPhase,
    setSelectedPlanId,
    setSelectedCarrierName,
    setZipCode,
    setSelectedProvider,
    setProviderParams,
    setWeekStartDate,
    setSelectedDate,
    setSelectedTimeslot,
  } = useZocdocBookingStore();
  const { track } = useAnalytics();
  const conversationId = useCurrentConversationId();

  const availabilityParams =
    selectedProvider && weekStartDate && bookingContent
      ? {
          provider_location_id: selectedProvider.provider_location_id,
          visit_reason_id: bookingContent.visit_reason_id,
          start_date: weekStartDate,
          end_date: addDaysYMD(weekStartDate, 6),
        }
      : null;

  const {
    data: providers = [],
    isLoading: providersLoading,
    isSuccess: providersSuccess,
    isError: isProvidersError,
  } = useGetProviders(
    providerParams ?? ({} as GetProvidersZocdocProvidersGetParams),
    {
      query: { enabled: isOpen && !!providerParams },
    },
  );

  const {
    data: availabilityData,
    isLoading: availabilityLoading,
    isSuccess: availabilitySuccess,
    isFetching: availabilityFetching,
    isError: isAvailabilityError,
  } = useGetAvailability(availabilityParams!, {
    query: { enabled: isOpen && !!availabilityParams },
  });

  const { data: reviewsData } = useGetProviderReviews(
    selectedProvider?.provider_id ?? "",
    { query: { enabled: isOpen && !!selectedProvider?.provider_id } },
  );

  useEffect(() => {
    if (providerParams && providersSuccess && phase === "zip") {
      track("superclinical-__zocdoc-providers-shown", {
        event_context: {
          conversation_id: conversationId,
          providers_shown: providers.length,
          zip_code: providerParams.zip_code,
          specialty_id: providerParams.specialty_id,
        },
      });
      setPhase("providers");
    }
  }, [
    providerParams,
    providersSuccess,
    phase,
    setPhase,
    providers,
    track,
    conversationId,
  ]);

  const [providerJustSelected, setProviderJustSelected] = useState(false);

  useEffect(() => {
    if (
      providerJustSelected &&
      availabilityParams &&
      availabilitySuccess &&
      phase === "providers"
    ) {
      setPhase("availability");
      setProviderJustSelected(false);
    }
  }, [
    providerJustSelected,
    availabilityParams,
    availabilitySuccess,
    phase,
    setPhase,
  ]);

  if (!bookingContent) return null;

  const handlePlanSelected = (
    planId: string | null,
    carrierName: string | null,
  ) => {
    setSelectedPlanId(planId);
    setSelectedCarrierName(carrierName);
    setPhase("zip");
  };

  const handleSearchProviders = (zip: string) => {
    setZipCode(zip);
    setProviderParams({
      zip_code: zip,
      specialty_id: bookingContent.specialty_id,
      ...(selectedPlanId ? { insurance_plan_id: selectedPlanId } : {}),
    });
  };

  const handleSelectProvider = (provider: typeof selectedProvider) => {
    setSelectedProvider(provider);
    setWeekStartDate(todayYMD());
    setProviderJustSelected(true);
  };

  if (phase === "insurance") {
    return <InsurancePhase onPlanSelected={handlePlanSelected} />;
  }

  if (phase === "zip") {
    return (
      <ZipPhase
        initialZipCode={zipCode}
        onSearch={handleSearchProviders}
        onBack={() => setPhase("insurance")}
        providersLoading={providersLoading}
        isProvidersError={isProvidersError}
      />
    );
  }

  if (phase === "providers") {
    return (
      <ProvidersPhase
        providers={providers}
        carrierName={selectedCarrierName}
        onSelectProvider={handleSelectProvider}
        onChangeInsurance={() => {
          setPhase("insurance");
          setProviderParams(null);
          setSelectedProvider(null);
          setWeekStartDate(null);
        }}
        onChangeZip={() => {
          setPhase("zip");
          setProviderParams(null);
          setSelectedProvider(null);
          setWeekStartDate(null);
        }}
        availabilityLoading={availabilityLoading}
        isAvailabilityError={isAvailabilityError}
      />
    );
  }

  if (phase === "day" && selectedDate) {
    return (
      <DayPhase
        selectedProvider={selectedProvider!}
        carrierName={selectedCarrierName}
        reviews={reviewsData ?? null}
        selectedDate={selectedDate}
        timeslots={availabilityData?.timeslots ?? []}
        onSelectTimeslot={(slot) => {
          setSelectedTimeslot(slot);
          setPhase("confirm");
        }}
        onBack={() => {
          setSelectedDate(null);
          setPhase("availability");
        }}
      />
    );
  }

  if (phase === "confirm" && selectedTimeslot) {
    return (
      <ConfirmPhase
        selectedProvider={selectedProvider!}
        carrierName={selectedCarrierName}
        reviews={reviewsData ?? null}
        timeslot={selectedTimeslot}
        eventId={eventId!}
        closeModal={close}
        onBack={() => {
          setSelectedTimeslot(null);
          setPhase("day");
        }}
      />
    );
  }

  return (
    <AvailabilityPhase
      selectedProvider={selectedProvider!}
      carrierName={selectedCarrierName}
      reviews={reviewsData ?? null}
      timeslots={availabilityData?.timeslots ?? []}
      weekStartDate={weekStartDate!}
      isLoading={availabilityFetching}
      onChangeWeek={setWeekStartDate}
      onSelectDate={(date) => {
        setSelectedDate(date);
        setPhase("day");
      }}
      onBack={() => {
        setPhase("providers");
        setSelectedProvider(null);
        setWeekStartDate(null);
      }}
    />
  );
};

export default function ZocdocBookingModal() {
  const isOpen = useZocdocBookingStore((s) => s.isOpen);
  const close = useZocdocBookingStore((s) => s.close);
  const reset = useZocdocBookingStore((s) => s.reset);
  const conversationId = useCurrentConversationId();

  useEffect(() => {
    reset();
  }, [conversationId, reset]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(nextOpen: boolean) => !nextOpen && close()}
      modal
    >
      <Dialog.Adapt when="maxLg">
        <Sheet
          modal
          disableDrag
          dismissOnSnapToBottom={false}
          snapPoints={[98]}
        >
          <Sheet.Frame padding="$300" flex={1}>
            <Adapt.Contents />
          </Sheet.Frame>
          <Sheet.Overlay
            backgroundColor="$fillOverlay"
            backdropFilter="blur(8px)"
          />
        </Sheet>
      </Dialog.Adapt>
      <Dialog.Portal>
        <Dialog.Overlay
          backgroundColor="$fillOverlay"
          backdropFilter="blur(8px)"
        />
        <Dialog.Content
          width="100%"
          maxWidth={560}
          maxHeight={680}
          borderRadius="$200"
          padding="$300"
          overflow="hidden"
        >
          <ZocdocBookingModalBody />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
